<script setup>
/* global axios */
import { ref, watch, onMounted, onUnmounted } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAccount } from 'dashboard/composables/useAccount';
import { useAlert } from 'dashboard/composables';
import NextButton from 'dashboard/components-next/button/Button.vue';

const props = defineProps({
  inbox: {
    type: Object,
    required: true,
  },
});

const { t } = useI18n();
const { accountId } = useAccount();

const state = ref(null);
const qrData = ref(null);
const isLoggingOut = ref(false);
const errorMessage = ref(null);

let stateTimer = null;
let qrTimer = null;

const baseUrl = () =>
  `/api/v1/accounts/${accountId.value}/inboxes/${props.inbox.id}/green_api`;

async function fetchState() {
  try {
    const res = await axios.get(`${baseUrl()}/state`);
    state.value = res.data.state;
  } catch {
    errorMessage.value = t(
      'INBOX_MGMT.ADD.WHATSAPP.GREEN_API.AUTHORIZATION.ERROR_FETCHING'
    );
  }
}

async function fetchQr() {
  try {
    const res = await axios.get(`${baseUrl()}/qr`);
    if (res.data.error) {
      errorMessage.value = t(
        'INBOX_MGMT.ADD.WHATSAPP.GREEN_API.AUTHORIZATION.ERROR_FETCHING'
      );
      return;
    }
    errorMessage.value = null;
    if (res.data.type === 'alreadyLogged') {
      state.value = 'authorized';
      qrData.value = null;
    } else {
      qrData.value = res.data;
    }
  } catch {
    errorMessage.value = t(
      'INBOX_MGMT.ADD.WHATSAPP.GREEN_API.AUTHORIZATION.ERROR_FETCHING'
    );
  }
}

function startQrPolling() {
  fetchQr();
  qrTimer = setInterval(fetchQr, 10000);
}

function stopQrPolling() {
  clearInterval(qrTimer);
  qrTimer = null;
  qrData.value = null;
}

watch(state, newState => {
  const needsQr = newState === 'notAuthorized' || newState === 'starting';
  if (needsQr && !qrTimer) {
    startQrPolling();
  } else if (!needsQr && qrTimer) {
    stopQrPolling();
  }
});

async function handleLogout() {
  // eslint-disable-next-line no-alert
  if (
    !window.confirm(
      t('INBOX_MGMT.ADD.WHATSAPP.GREEN_API.AUTHORIZATION.DISCONNECT')
    )
  )
    return;
  isLoggingOut.value = true;
  try {
    const res = await axios.post(`${baseUrl()}/logout`);
    if (res.data.error) {
      useAlert(
        t('INBOX_MGMT.ADD.WHATSAPP.GREEN_API.AUTHORIZATION.ERROR_FETCHING')
      );
    } else {
      // Wait briefly for Green-API to process, then re-fetch state.
      // Optimistically setting state to notAuthorized would flicker back to
      // 'authorized' on the next 5s poll if Green-API hasn't yet recorded the logout.
      setTimeout(() => fetchState(), 1500);
    }
  } catch {
    useAlert(
      t('INBOX_MGMT.ADD.WHATSAPP.GREEN_API.AUTHORIZATION.ERROR_FETCHING')
    );
  } finally {
    isLoggingOut.value = false;
  }
}

onMounted(() => {
  fetchState();
  stateTimer = setInterval(fetchState, 5000);
});

onUnmounted(() => {
  clearInterval(stateTimer);
  clearInterval(qrTimer);
});
</script>

<template>
  <div
    class="flex flex-col gap-4 rounded-lg border border-n-weak bg-n-solid-1 p-4"
  >
    <p class="text-heading-s font-semibold text-n-slate-12">
      {{ $t('INBOX_MGMT.ADD.WHATSAPP.GREEN_API.AUTHORIZATION.TITLE') }}
    </p>

    <!-- Error -->
    <div v-if="errorMessage" class="text-body-main text-ruby-500">
      {{ errorMessage }}
    </div>

    <!-- Loading -->
    <div v-if="state === null" class="text-body-main text-n-slate-11">
      {{ $t('INBOX_MGMT.ADD.WHATSAPP.GREEN_API.AUTHORIZATION.LOADING') }}
    </div>

    <!-- Authorized -->
    <div v-else-if="state === 'authorized'" class="flex flex-col gap-3">
      <div class="flex items-center gap-2">
        <span
          class="i-ri-checkbox-circle-fill text-lg text-green-500"
          aria-hidden="true"
        />
        <span class="text-body-main text-n-slate-12">
          {{ $t('INBOX_MGMT.ADD.WHATSAPP.GREEN_API.AUTHORIZATION.AUTHORIZED') }}
        </span>
      </div>
      <div>
        <NextButton :is-loading="isLoggingOut" danger @click="handleLogout">
          {{ $t('INBOX_MGMT.ADD.WHATSAPP.GREEN_API.AUTHORIZATION.DISCONNECT') }}
        </NextButton>
      </div>
    </div>

    <!-- Blocked -->
    <div v-else-if="state === 'blocked'" class="text-body-main text-ruby-500">
      {{ $t('INBOX_MGMT.ADD.WHATSAPP.GREEN_API.AUTHORIZATION.STATE_BLOCKED') }}
    </div>

    <!-- Sleep mode -->
    <div
      v-else-if="state === 'sleepMode'"
      class="text-body-main text-yellow-600"
    >
      {{ $t('INBOX_MGMT.ADD.WHATSAPP.GREEN_API.AUTHORIZATION.STATE_SLEEPING') }}
    </div>

    <!-- Starting or not authorized — show QR scan flow -->
    <div v-else class="flex flex-col gap-3">
      <p v-if="state === 'starting'" class="text-body-main text-n-slate-11">
        {{
          $t('INBOX_MGMT.ADD.WHATSAPP.GREEN_API.AUTHORIZATION.STATE_STARTING')
        }}
      </p>
      <p class="text-label-small text-n-slate-11">
        {{
          $t('INBOX_MGMT.ADD.WHATSAPP.GREEN_API.AUTHORIZATION.SCAN_INSTRUCTION')
        }}
      </p>
      <div v-if="qrData">
        <img
          :src="`data:image/png;base64,${qrData.message}`"
          :alt="$t('INBOX_MGMT.ADD.WHATSAPP.GREEN_API.AUTHORIZATION.TITLE')"
          class="h-48 w-48 rounded border border-n-weak"
        />
      </div>
      <div v-else class="text-label-small text-n-slate-11">
        {{ $t('INBOX_MGMT.ADD.WHATSAPP.GREEN_API.AUTHORIZATION.LOADING') }}
      </div>
    </div>
  </div>
</template>
